import {siteSecondary, installPinSprites} from './pins.mjs';
import {availability} from './availability.mjs';
import {palette} from './palette.mjs';
import {installPointer} from './map-pointer.mjs';
import {addSiteLayers, installSiteClick, placePoiYield, syncHitFilter, syncSelectedPin, syncSiteFocus, syncPinVisibility, DOT_LAYER_IDS} from './site-layers.mjs';
import {dotMode} from './dot-variants.mjs';
import * as maplibre from './vendor/maplibre-gl-6.16.0/maplibre-gl.mjs';
import {startLive} from './live.mjs';
import {readFilters, filterParams, activeFilters} from './filters.mjs';
import {geolocate} from './geolocate.mjs';
import {sheets} from './sheets.mjs';
import {isWide} from './layout.mjs';
import {selectedSite, selectedScale} from './selected-site.mjs';
import {panelMap} from './panel-map.mjs';
import {installIcons} from './icons.mjs';
import {S, renderUI, translateUI} from './strings.mjs';
import {preferences} from './settings.mjs';
import {appearance, installSettings, installControls} from './app-settings.mjs';
import {placeSearch, placeTransition} from './place-search.mjs';
import {installAttribution} from './attribution.mjs';
import {mapRenderer} from './map-renderer.mjs';
import {MapPipeline} from './map-pipeline.mjs';
import {basemapStart} from './basemap-start.mjs';
import {cameraFromURL,wrapLongitude} from './map-location.mjs';
import {detailModel, renderDetails, resetTitle, fitTitle, fitCards, fitActions} from './details-view.mjs';
import {shareData, shareSite, SITE_ID} from './share.mjs';
import {loadLanguage} from './i18n.mjs';
import {installHitSprites, readHits, fitCamera} from './hits.mjs';
import {mapLoading, basemapView, SHOW_DELAY} from './map-loading.mjs';
import {sessionId, startSearchSession} from './search-session.mjs';
import {startSearch} from './search-start.mjs';
import {SEARCH_MAX_ZOOM} from './search-camera.mjs';
import {frameView} from './frame-view.mjs';
import {NEAR_ZOOM} from './follow.mjs';
import {readSearchOrigin, writeSearchOrigin, initialLocate, fitSearchOrigin, searchOriginMarker} from './search-origin.mjs';
import {renderSearchChip} from './search-chip.mjs';
import {toaster} from './toast.mjs';
import {livePopover} from './live-popover.mjs';
import {loadPrices} from './prices.mjs';
import {startDetailRefresh} from './detail-refresh.mjs';
import {absoluteRequest, styleURL, styleSwitcher} from './map-styles.mjs';

const $ = id => document.getElementById(id);
const params = new URLSearchParams(location.search);
// GPU dots; ?dots=circle keeps the circle layer.
const dotsMode = dotMode(location.search);
let tabStorage = null; try { tabStorage = sessionStorage; } catch { /* URL fallback. */ }
const currentSession = sessionId(params, tabStorage);
const initialSequence = Number(params.get('session_seq'));
let sessionSequence = Number.isSafeInteger(initialSequence) && initialSequence > 0 ? initialSequence : 0, searchRevision = 0;
let tileLoading, tilesReady = false, originMarker, earlyPins = null;
const initialCamera = cameraFromURL(params), hasPosition = !!initialCamera.position;
const origin = readSearchOrigin(params);
const filters = readFilters(params);
// Search results (assistant link): ids in rank order.
let hits = readHits(params.get('hits'));
// Shared link: open this site's details.
const sharedSite = SITE_ID.test(params.get('site') ?? '') ? params.get('site') : null;
let hitsFitted = !origin.point && !!(currentSession && sessionSequence && hasPosition), lastDetail = null;
const media = matchMedia('(prefers-color-scheme: dark)');
let storage = null; try { storage = localStorage; } catch { /* Continue without storage. */ }
let prefs = preferences(storage, media.matches);
let profile = palette(prefs.theme);
await loadLanguage(prefs.lang);
renderUI();
document.documentElement.dataset.theme = prefs.theme;
let points = [], sitesById = new Map(), map, liveControl, pipeline, locator, staleDays = 7;
let resolveCatalog;
const catalogLoaded = new Promise(resolve => { resolveCatalog = resolve; });
let catalogReady = false, sourceReady = false, detailId = null, detailRequest = 0;
let panelCamera, panelState, selection, search, renderer;
const {openSheet, closeSheet, toggleSheet} = sheets(() => { detailId = null; pipeline?.schedule(); syncHitSelection(); detailRequest++; panelCamera?.select(null); selection?.clear(); },
  {onChange: state => { panelState = state; panelCamera?.sync(); }});
let syncFilters, liveInfo, priceRequest = 0;
const toast = toaster($('message'));
// Live page state for app-settings.mjs.
const app = {prefs, toast, refreshIndex, filterBadge, renderHitsChip, details, syncFilters: () => syncFilters(),
  get profile() { return profile; }, set profile(value) { profile = value; }, get map() { return map; }, get sourceReady() { return sourceReady; },
  get selection() { return selection; }, get pipeline() { return pipeline; }, get liveControl() { return liveControl; }, get liveInfo() { return liveInfo; },
  get locator() { return locator; }, get staleDays() { return staleDays; }, get detailId() { return detailId; }};
const look = appearance(app);
// level: info | warn | error; key groups messages that a later event resolves.
const message = (text, level = 'info', key) => toast.show(text, level, key);

async function json(url, options) {
  const response = await fetch(url, options);
  if (!response.ok) throw new Error(S.dataError);
  return response.json();
}
function writeURL() {
  const c = map.getCenter(), p = new URLSearchParams();
  p.set('lat', c.lat.toFixed(5)); p.set('lon', wrapLongitude(c.lng).toFixed(5)); p.set('z', map.getZoom().toFixed(1));
  if (currentSession) { p.set('session_id', currentSession); if (sessionSequence > 0) p.set('session_seq', sessionSequence); }
  filterParams(filters, p);
  writeSearchOrigin(p, origin);
  if (dotsMode.param) p.set('dots', dotsMode.param);
  if (hits.length) p.set('hits', hits.join(','));
  if (locator && locator.state !== 'off' && locator.state !== 'centering') p.set('follow', '1');
  history.replaceState(null, '', `/map?${String(p).replaceAll('%2C', ',')}`);
}
function filterBadge() {
  const count = activeFilters(filters);
  $('filter-count').hidden = !count; $('filter-count').textContent = count;
  $('filter-button').setAttribute('aria-label', S.filterCount(count));
}
function addLayers() {
  addSiteLayers(map, {theme: prefs.theme, profile, focused: hits.length > 0, gpu: dotsMode.gpu});
  sourceReady = true; syncHitSelection(); syncTileLayers(); pipeline.restore();
  if (earlyPins && !pipeline.ready) renderer.apply({pins: earlyPins});
  installSiteClick(map, {backdrop: $('backdrop'), pipeline: () => pipeline, camera: options => panelCamera.camera(options), closeSheet, selected: () => detailId, details,
    failed: () => message(S.dataError, 'error')});
}
function syncHitSelection() {
  if (map) { syncHitFilter(map, detailId); syncSelectedPin(map, selection?.id); }
}
function notice(text) { const e = document.createElement('p'); e.className = 'site-notice'; e.textContent = text; return e; }
async function details(id, show = true, center = false, coords = null) {
  detailId = id; pipeline?.schedule(); syncHitSelection(); const request = ++detailRequest;
  if (show) {
    panelCamera?.select(null);
    lastDetail = null; resetTitle($('details-title'), S.sheetTitle.details); $('details-route').hidden = true; $('details-share').hidden = true;
    delete $('details-content').dataset.site;
    $('details-content').replaceChildren(notice(S.loading)); openSheet('details');
    if (coords) { selection?.show(id, coords); panelCamera?.select(coords); }
  }
  try {
    const d = await json(`/api/sites/${encodeURIComponent(id)}`);
    if (request !== detailRequest || detailId !== id) return;
    const c = locator?.position?.coords;
    const model = detailModel(d, {position: c ? [c.latitude, c.longitude] : null, staleDays});
    renderDetails(model, $('details-content'));
    lastDetail = model; fitTitle($('details-title'), model); fitCards($('details-content'));
    $('details-route').href = model.route; $('details-route').hidden = false; $('details-share').hidden = !model.id; fitActions($('details-share'));
    if (Number.isFinite(d.lat) && Number.isFinite(d.lon)) {
      if (center) map.easeTo(panelCamera.camera({center: [d.lon, d.lat], zoom: Math.max(map.getZoom(), NEAR_ZOOM), duration: 0}));
      if (show && !coords) { selection?.show(id, [d.lon, d.lat]); if (!center) panelCamera?.select([d.lon, d.lat]); }
    }
  } catch { if (request === detailRequest) $('details-content').replaceChildren(notice(S.detailsUnavailable)); }
}
// The price filter needs prices.bin for the current catalog; it is fetched once, when first used.
async function refreshIndex(full = false) {
  if (!catalogReady) return;
  const revision = searchRevision;
  if ((filters.maxPrice !== Infinity || prefs.secondary === 'price') && points.prices !== points.version) {
    const request = ++priceRequest, catalog = points;
    try { await loadPrices(catalog, fetch, buffer => pipeline.decodePrices(buffer)); selection?.refresh(); toast.resolve('prices'); }
    catch { if (request === priceRequest) message(S.pricesUnavailable, 'warn', 'prices'); }
    if (request !== priceRequest || catalog !== points || revision !== searchRevision) return;
    return pipeline.load(points, filters, hits).then(afterLoad);
  }
  const early = full && pipeline.takePreloaded(points, filters, hits);
  if (early) return early.then(afterLoad);
  return full || !pipeline.ready ? pipeline.load(points, filters, hits).then(afterLoad) : pipeline.filter(filters);
}
function afterLoad() { renderHitsChip(); return fitHits(); }
// Hit pins come from the worker's catalog before the nationwide model; the style layers take them once they exist.
function drawEarly(features) {
  if (pipeline.ready) return;
  earlyPins = features; if (sourceReady) renderer.apply({pins: features});
  renderHitsChip();
}
function searchEnv(revision, cap) {
  return {pipeline, hits, filters, origin, cap, early: filters.maxPrice === Infinity && prefs.secondary !== 'price', draw: drawEarly,
    view: () => frameView(map, {panelLeft: panelCamera.padding({left: 0}).left}), isCurrent: () => revision === searchRevision};
}
// Frame query point and hits in the free map area once; only GPS links auto-follow.
async function fitHits() {
  if (hitsFitted || (!hits.length && !origin.source) || !map) return;
  hitsFitted = true;
  const revision = searchRevision;
  const start = await startSearch(searchEnv(revision, hits.length ? SEARCH_MAX_ZOOM : map.getZoom()));
  if (!start) return;
  if (start.camera) map.jumpTo(start.camera);
  if (origin.autoGPS) await fitSearchOrigin(map, start.coords, origin, locator, safePadding, view => view, () => revision === searchRevision, false);
}
function safePadding() { return searchEnv(searchRevision).view().padding; }
function renderHitsChip() {
  renderSearchChip(hits, clearPlace, tilesReady && !!(earlyPins || pipeline?.ready));
}
function syncTileLayers() {
  if (!map || !sourceReady) return;
  if (map._w2cGpu) map._w2cGpu.visible = tilesReady;
  for (const id of map._w2cGpu ? [] : DOT_LAYER_IDS) map.getLayer(id) && map.setLayoutProperty(id, 'visibility', tilesReady ? 'visible' : 'none');
  syncPinVisibility(map, tilesReady);
}
async function applySearch(next, sequence) {
  const autoGPS = next.origin.autoGPS && (sessionSequence > 0 || params.has('session_id') || params.has('source'));
  searchRevision++; priceRequest++; pipeline.invalidate(); map.stop(); closeSheet();
  // Frame for the closed panel now.
  panelState = null; panelCamera?.sync?.();
  detailRequest++; detailId = null; lastDetail = null; selection?.clear(); panelCamera?.select(null);
  locator.disable(); // Stops an old GPS watch; manual Locate remains available.
  Object.assign(origin, next.origin); Object.assign(filters, next.filters); hits = next.hits;
  sessionSequence = sequence; hitsFitted = true; earlyPins = null;
  originMarker?.remove(); originMarker = searchOriginMarker(map, maplibre, origin, S.searchLocation); search?.show(origin.label);
  if (sourceReady) renderer.clear();
  syncFilters(); filterBadge(); renderHitsChip(); selection?.refresh();
  if (sourceReady) syncSiteFocus(map, hits.length > 0);
  tileLoading.begin('search');
  const revision = searchRevision, release = tileLoading.hold();
  let start;
  try {
    start = await startSearch(searchEnv(revision, hits.length ? SEARCH_MAX_ZOOM : NEAR_ZOOM));
    if (start?.camera) map.easeTo({...start.camera, duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 700});
  } finally { release(); }
  if (!start) return;
  writeURL();
  // A stored session replay on a normal page load must not request location.
  if (autoGPS) {
    map.stop();
    locator.follow(c => fitCamera(map, next.bounds.length ? next.bounds : [...next.bounds, origin.point],
      [c.longitude, c.latitude], safePadding(), NEAR_ZOOM)?.zoom ?? null);
  }
  await catalogLoaded;
  if (revision === searchRevision) refreshIndex(true).catch(() => {});
}
function pickPlace(place) { applySearch(placeTransition(place, filters), sessionSequence); }
function clearPlace() {
  searchRevision++; hitsFitted = true; earlyPins = null;
  Object.assign(origin, {source: null, point: null, label: null, autoGPS: false}); originMarker?.remove(); originMarker = null; search?.show(null);
  hits = []; renderHitsChip(); if (map) syncSiteFocus(map, false); selection?.refresh(); refreshIndex(true); if (map) writeURL();
}
function controls() {
  syncFilters = installControls(filters, {toggleSheet, closeSheet, filterBadge, changed: () => { selection?.refresh(); filterBadge(); refreshIndex(); if (map) writeURL(); },
    share: () => { if (lastDetail?.id) shareSite(shareData(lastDetail, location.origin), message); }});
}
// Before the map exists the same delayed spinner covers config and font loading; delays count from navigation start
// (index.html's inline boot spinner appears after SHOW_DELAY, so a late module start shows it at once).
const earlySpinner = setTimeout(() => { $('map-loading').hidden = false; }, Math.max(0, SHOW_DELAY - performance.now()));
async function main() {
  await Promise.all([document.fonts.load('600 14px Inter'),document.fonts.load('700 18px Inter')]);
  installIcons(); controls(); renderHitsChip();
  search = placeSearch({onPick: pickPlace, onClear: clearPlace, center: () => map?.getCenter(),
    wide: () => isWide(), coarse: () => matchMedia('(pointer: coarse)').matches});
  search.show(origin.label);
  const initial = initialCamera.position || origin.point || [10.45, 51.16];
  const configController = new AbortController();
  const configDeadline = setTimeout(() => configController.abort(), 15000);
  let config;
  try { config = await json('/api/config', {signal:configController.signal}); } finally { clearTimeout(configDeadline); }
  translateUI(config.status_max_age_days);
  staleDays = Number(config.status_max_age_days) || staleDays;
  look.renderLegend();
  map = new maplibre.Map({container: 'map',
    fadeDuration: 0, center: initial,
    minZoom: 0, zoom: hasPosition || origin.point ? initialCamera.zoom : 5.5,
    attributionControl: false, locale: S.locale, transformRequest: absoluteRequest});
  const startup = basemapStart(map, () => tilesReady && pipeline?.ready && map.isSourceLoaded('sites'), {restored: () => placePoiYield(map)});
  map.setStyle(styleURL(prefs.theme), {transformStyle: startup.transform, validate: false});
  panelCamera = panelMap(map, () => panelState); panelCamera.sync();
  originMarker = searchOriginMarker(map, maplibre, origin, S.searchLocation);
  tileLoading = mapLoading(map, state => {
    tilesReady = basemapView(state, tilesReady); syncTileLayers(); renderHitsChip();
    if (tilesReady) pipeline?.schedule();
  }, {initialDelay: Math.max(0, SHOW_DELAY - performance.now()), reload: () => retryStyle()});
  clearTimeout(earlySpinner);
  // MapLibre's ResizeObserver can settle after the window resize: use its projection.
  map.on('resize', () => {panelCamera.resize();selection?.refresh();});
  selection = selectedSite(map, maplibre, id => { const site = sitesById.get(id); return site ? {counts: availability(site, filters), ...siteSecondary(site), focused: !hits.length || hits.includes(id), rank:hits.indexOf(id)+1} : null; }, () => ({theme: prefs.theme, profile, secondary:prefs.secondary, scale: selectedScale(isWide())}), closeSheet, id => syncSelectedPin(map, id));
  renderer = mapRenderer(map, () => ({ready:sourceReady,prefs,profile,detailId,selection}));
  pipeline = new MapPipeline(map, renderer.apply, () => message(S.dataError, 'error', 'data'));
  pipeline.gpu = dotsMode.gpu; pipeline.onDecoded = () => fitHits();
  pipeline.early = () => filters.maxPrice === Infinity && prefs.secondary !== 'price' ? {filters, hits} : null;
  const attribution = installAttribution(map);
  attribution.setStyle();
  const switchStyle = styleSwitcher(map, {fetchJSON: json, requested: () => tileLoading.begin('style-request'), beforeApply: () => { startup.cancel(); tileLoading.begin('style'); sourceReady = false; },
    applied: () => attribution.setStyle(), failed: () => tileLoading.fail()});
  installPinSprites(map, () => profile); installHitSprites(map, () => profile);
  installPointer(map);
  map.addControl(new maplibre.NavigationControl({showCompass: false}), 'top-right');
  map.on('style.load', () => { sourceReady = false; addLayers(); });
  map.on('moveend', writeURL);
  // Rebuild failed source caches with the desired style.
  const retryStyle = () => switchStyle(styleURL(prefs.theme), {reload:true});
  $('map-retry-button').onclick = retryStyle;
  locator = geolocate(map, maplibre, $('position-button'), text => message(text, 'warn', 'position'),
    {camera: options => panelCamera.camera(options), start: initialLocate(origin, {hasHits: !!hits.length}), onChange: () => { if (locator) writeURL(); }});
  addEventListener('resize', () => { selection?.refresh(); attribution.place(); if (detailId && lastDetail) { fitTitle($('details-title'), lastDetail); fitCards($('details-content')); fitActions($('details-share')); } });
  if (sharedSite) details(sharedSite, true, true);
  installSettings(app, {switchStyle, media, storage}, look);
  startDetailRefresh(() => details(detailId, false), {shouldRead: () => !!detailId && prefs.live && !document.hidden});
  liveInfo = livePopover($('live-badge'), $('live-popover'));
  look.syncSettings();
  startSearchSession(currentSession, applySearch, {initialSeq: sessionSequence});
  liveControl = startLive(null, (catalog, updates, kind) => {
    points = catalog; if(!updates) sitesById = new Map(points.map(p=>[p.id,p])); catalogReady = true; resolveCatalog(); selection?.refresh();
    if (updates) (kind === 'prices' ? pipeline.prices(updates) : pipeline.delta(updates)); else { if(!prefs.live)toast.resolve('live'); refreshIndex(true); }
    if (detailId && (!updates || points.find(p => p.id === detailId)?.points.some(p => updates.some(([i]) => i === p.index)))) details(detailId, false);
  }, text => message(text, 'error', 'live'), {enabled:prefs.live,codec:pipeline,
    // After a long absence: the current price table, if loaded for this catalog.
    prices: async catalog => {
      if (catalog.prices !== catalog.version) return null;
      const response = await fetch('/api/prices.bin', {cache: 'no-cache'});
      if (!response.ok) throw Error('prices');
      const table = await pipeline.decodePrices(await response.arrayBuffer());
      return table.version === catalog.version ? table.cents : null;
    }, onState:state=>{
    $('live-badge').dataset.state=state; liveInfo.setState(state);
    if(state==='open') toast.resolve('live');
  }});
}
main().catch(() => { clearTimeout(earlySpinner); tileLoading?.stop(); $('map-loading').hidden = true; $('map-retry').hidden = false; $('map-retry-button').onclick = () => location.reload(); message(S.startError, 'error'); });

export {map};
